import { useMemo } from 'react';
import type { GetStaticPaths, GetStaticProps } from 'next';
import Button from 'epn-ui/umd/dist/components/Button';
import Table from 'epn-ui/umd/dist/components/Table';
import { toast } from 'react-toastify';
import useI18n, { getLocalePaths } from '@i18n';
import apiHooks, { ICreateUseRequestInfiniteResponse } from '@apiHooks';
import routes from '@config/routes.json';
import Actions from '@components/pages/tools/feeds/Actions';

import BaseTableMobile from '@base/BaseTableMobile';
import api from '@api';

import BaseToast from '@base/BaseToast';

import { IGetFeedsDataFormatted, IGetOffersDataFormatted } from '@api/types';

// HOCs
import WithRole, { rolesEnum } from '@HOCs/WithRole';

const { useFeeds, useOffers } = apiHooks;

const Feeds: React.FC = () => {
    const { t, locale } = useI18n();
    const {
        data: feeds,
        mutate: mutateFeeds,
        isLoading: isFeedsLoading,
        isReachingEnd,
        nextPage,
    } = useFeeds({
        params: {
            limit: 10,
            sort: '-created_at',
        },
    }) as ICreateUseRequestInfiniteResponse<IGetFeedsDataFormatted[]>;

    const { data: offers, isLoading: isOffersLoading, error } = useOffers({
        condition: feeds.length > 0,
        params: {
            limit: feeds.length,
            lang: locale,
            viewRules: 'role_user',
            ids: feeds.map(feed => feed.offerId),
        },
    }) as ICreateUseRequestInfiniteResponse<IGetOffersDataFormatted[]>;

    const columnNames = [
        {
            title: t('Title'),
            dataKey: 'name',
            textAlign: 'left',
            maxWidth: 150,
        },
        {
            title: t('Offer'),
            dataKey: 'offerName',
            textAlign: 'left',
        },
        {
            title: t('Uploading date'),
            dataKey: 'createdAt',
            textAlign: 'left',
        },
        {
            title: t('Actions'),
            dataKey: 'actions',
        },
    ];

    const onDeleteOfferById = async (id: number) => {
        try {
            await api.feeds.deleteFeed({
                urlParams: {
                    id,
                },
            });

            await mutateFeeds();

            toast.success(<BaseToast text={t('Feed was deleted')} type="success" />, {
                toastId: id,
            });
        } catch (e) {
            toast.success(<BaseToast text={t('Something went wrong')} type="error" />, {
                toastId: id,
            });
        }
    };

    const tableFeeds = useMemo(() => {
        if (offers.length > 0 && feeds.length > 0) {
            return feeds.map(({ id, name, offerId, createdAt, downloadLink }) => {
                const offer = offers.find(currentOffer => currentOffer.id === offerId);

                return {
                    id,
                    name,
                    createdAt: <Table.DateCell date={createdAt} />,
                    offerName: offer?.name,
                    actions: (
                        <Actions
                            id={id}
                            downloadLink={downloadLink}
                            updateLink={`/${locale}${routes.tools.feed.edit.replace(
                                ':id',
                                String(id),
                            )}`}
                            onDeleteOfferById={onDeleteOfferById}
                        />
                    ),
                };
            });
        }

        return [];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [offers, feeds]);

    return (
        <div>
            <div className="flex flex-col items-start justify-between xs:items-center xs:flex-row">
                <h4 className="font-medium text-blueGray-600 text-h2">{t('Feed list')}</h4>
                <Button
                    className="mt-4 xs:mt-0"
                    type="primary"
                    size="medium"
                    to={`/${locale}${routes.tools.feed.create}`}
                >
                    {t('Create uploading')}
                </Button>
            </div>
            <p className="mt-6 text-blueGray-600 text-p1">
                {t(
                    'Download with feed of products designed to work with a lot of products. You can create affiliate stores based on these downloads, or you can use them in contextual advertising systems, automatically forming ads.',
                )}
            </p>
            <div className="mt-8 mb-36">
                <div className="hidden xs:block max-w-[95vw]">
                    <Table
                        isLoading={isFeedsLoading || isOffersLoading}
                        columns={columnNames}
                        maxTableHeightClasses=""
                        rowHeight={80}
                        data={tableFeeds}
                        loadMoreParams={{
                            hasNextPage: !isReachingEnd,
                            buttonText: t('Load more'),
                            onLoadMoreRows: nextPage,
                        }}
                        emptyParams={{
                            buttonText: t('Create uploading'),
                            message: t('Create your first uploading. To create it, click a button'),
                            href: `/${locale}${routes.tools.feed.create}`,
                        }}
                        errorParams={{
                            requestId: error?.response?.headers?.get('request-id'),
                        }}
                    />
                </div>
                <BaseTableMobile
                    className="xs:hidden max-w-[93vw]"
                    isLoading={isFeedsLoading || isOffersLoading}
                    data={tableFeeds}
                    columns={columnNames}
                    loadMoreParams={{
                        hasNextPage: !isReachingEnd,
                        buttonText: t('Load more'),
                        onLoadMoreRows: nextPage,
                    }}
                    emptyParams={{
                        buttonText: t('Create uploading'),
                        message: t('Create your first uploading. To create it, click a button'),
                        href: `/${locale}${routes.tools.feed.create}`,
                    }}
                    errorParams={{
                        requestId: error?.response?.headers?.get('request-id'),
                    }}
                />
            </div>
        </div>
    );
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
    return { props: { locale: params?.locale } };
};

export const getStaticPaths: GetStaticPaths = async () => {
    return { paths: getLocalePaths(), fallback: false };
};

export default WithRole(Feeds, [rolesEnum.user]);
